import { z } from 'zod';
import { formatCount } from '@packages/format';
import { createAggregateQuerySchema, createAggregateResponseSchema } from './aggregate.js';
import { formatDeprecatedActivityTypeGuidance, formatIsDisbursementDefaults, formatUnitTypeDefaultsAndOverrides } from '../activity-types.js';
import { BusinessEntitySchema, EntityTypeSchema, businessEntityIdentityTaxInput } from './account.js';
import { CalendarDateSchema, ClientProjectStatusSchema, ClientStatusSchema, LowercaseUuidSchema, MAX_MINOR_UNITS, MAX_UNITS, PaginationQuerySchema, UnitTypeSchema, createPaginatedResponseSchema, saPhoneNumberSchema, saVatNumberSchema, writeWarningsFields } from './common.js';
import { nullableText } from '../sanitised-text.js';
import { heldName } from '../entity-document-name.js';
// =============================================================================
// Client
// =============================================================================
// Source enum shared across client, client-project, and client-project-activity entities
export const EntitySourceSchema = z.enum(['WEB_APP', 'CHAT', 'EMAIL_AI_DETECTION', 'CALENDAR', 'CSV_IMPORT']);
export const ClientEmailDtoSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    email: z.string(),
    isPrimary: z.boolean(),
    type: z.enum(['client', 'associated']),
    name: z.string().nullable(),
    createdAt: z.string(),
});
export const ClientEmailAddressSchema = z.email('Enter a valid email address').max(254, 'Email must be 254 characters or less');
// The contact-name constraint on an add-email row. Exported alongside
// ClientEmailAddressSchema so every surface that validates the email field of
// this row (backend route, ClientFormPage's inline add, RecipientPicker's
// inline add) validates the sibling name field with the identical rule
// instead of hand-rolling a second `> 200` check that can drift from this one.
export const ClientEmailContactNameSchema = nullableText(200, 'Name must be 200 characters or less');
export const AddClientEmailBodySchema = z.object({
    email: ClientEmailAddressSchema,
    name: ClientEmailContactNameSchema,
});
// One address supplied at client creation. Shares AddClientEmailBodySchema's
// field schemas but is its own strict object: AddClientEmailBodySchema predates
// the strict-request convention and is still a plain object.
export const CreateClientEmailInputSchema = z.strictObject({
    email: ClientEmailAddressSchema,
    name: ClientEmailContactNameSchema,
});
export const CREATE_CLIENT_MAX_EMAILS = 10;
// Only isPrimary: true is accepted. false is rejected by schema validation (returns 400).
// The updateClientEmail route uses mcp.overrides.body to inject isPrimary: true, so Gemini never sees this literal.
/* eslint-disable zod-compat/no-zod-boolean-literal */
export const UpdateClientEmailBodySchema = z.object({
    isPrimary: z.literal(true),
});
// Required key, nullable value: pass null to clear the name, a string to set it.
// This route is web-only and is not exposed as an MCP tool.
export const UpdateClientEmailNameBodySchema = z.object({
    name: z.string().max(200, 'Name must be 200 characters or less').nullable(),
});
export const ListClientEmailsResponseSchema = z.object({
    emails: z.array(ClientEmailDtoSchema),
});
export const ClientEmailResponseSchema = z.object({
    email: ClientEmailDtoSchema,
});
// One live section 86(4) trust investment account that would block this
// client's archive. Carries just enough (id + label) for the caller to name
// the account in a sentence, matching the wording archiveClient's 409 already
// uses ("This client holds a live section 86(4) trust investment account
// (<label>)...").
export const ClientBlockingTrustInvestmentAccountSchema = z.object({
    id: z.string(),
    label: z.string(),
});
export const ClientSchema = z.object({
    id: z.string(),
    name: z.string(),
    primaryEmail: z.string().nullable(),
    emails: z.array(ClientEmailDtoSchema),
    phone: z.string().nullable(),
    businessEntityId: z.string().nullable(),
    businessEntity: BusinessEntitySchema.nullable().optional(),
    /**
     * The Account holding this client's legal entity's own books, when the firm
     * administers that entity (a business rescue, an insolvency, a deceased
     * estate). Null means the firm does not keep this entity's books - which is
     * also the reading for every client created before the feature shipped, so
     * there is no "unknown" state to distinguish.
     *
     * The client hub reads this to decide between offering to set the
     * books up and offering to open them.
     *
     * `.optional()` alongside `.nullable()`, matching `AccountSchema`'s
     * `administeredBy`: a newly deployed frontend bundle can be talking to a
     * server revision that predates the field, and a required key would fail
     * every client parse for the length of that window. Read an absent key
     * exactly as null.
     */
    administeredAccountId: z.string().nullable().optional(),
    defaultRateMinorUnits: z.int().nullable(),
    defaultRateCurrency: z.string(),
    status: ClientStatusSchema,
    source: EntitySourceSchema,
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    lastActivityDate: z.iso.datetime().nullable(),
    // Populated on the client detail response (GET .../clients/:clientId) only,
    // driven by the SAME reader (`findActiveTrustInvestmentAccountsForClient`)
    // the archiveClient 409 guard uses, so the UI's "can I offer delete" answer
    // can never drift from the guard's "will I refuse delete" answer. An empty
    // array means no blocking account; length distinguishes one from several.
    // Optional so list/create/update responses, which reuse this same schema
    // without running the extra query, remain valid without a value - a client
    // list row would otherwise cost one query per row (N+1).
    blockingTrustInvestmentAccounts: z.array(ClientBlockingTrustInvestmentAccountSchema).optional(),
});
/**
 * Response for `GET .../clients/:clientId/wip-total` and
 * `GET .../client-projects/:clientProjectId/wip-total`. WIP means APPROVED
 * activity only, matching the dashboard's definition (`findWipTotal`): DRAFT
 * activities are visible only to their creator, so a DRAFT+APPROVED figure
 * shown firm-wide would disagree with the per-member activity list. This is
 * deliberately a different question from `unbilledTotalMinorUnits` elsewhere
 * on this file (the invoice builder's DRAFT+APPROVED "what can I bill" total),
 * which is unaffected by this definition.
 *
 * Deliberately carries no currency: the summed activities can span more than
 * one currency, and the UI renders the figure using the client's configured
 * default currency rather than a value this response would have to fabricate.
 */
export const ClientWipTotalResponseSchema = z.object({
    wipTotalMinorUnits: z.number(),
});
export const UpsertClientBusinessEntityInputSchema = z.object({
    expectedUpdatedAt: z.string().datetime().nullable().optional(),
    // Required key; blank means no legal name and is stored as null.
    legalName: z.string().max(300, 'Legal name must be 300 characters or less').nullable().transform(v => heldName(v)),
    entityType: EntityTypeSchema.optional().nullable(),
    registrationNumber: nullableText(50, 'Registration number must be 50 characters or less'),
    vatNumber: saVatNumberSchema,
    ...businessEntityIdentityTaxInput,
    addressLine1: nullableText(300),
    addressLine2: nullableText(300),
    addressCity: nullableText(100),
    addressProvince: nullableText(100),
    addressPostalCode: nullableText(20),
    addressCountry: z.string().max(100).optional(),
});
/** New inline writes reject unknown fields; the standalone upsert retains its legacy parser. */
const ClientBusinessEntityInputSchema = z.strictObject(UpsertClientBusinessEntityInputSchema.shape);
const ClientNameTargetInputSchema = z.enum(['client', 'trading']).optional().describe('Where an explicit name is written: client changes only the client label; trading also sets the entity trading name. Not a stored business classification. Omit on unrelated updates.');
export const CreateClientInputSchema = z.object({
    idempotencyKey: z.string().min(1).max(255),
    name: z.string().min(1, 'Client name is required').max(200, 'Name must be 200 characters or less'),
    businessEntity: ClientBusinessEntityInputSchema.optional(),
    nameTarget: ClientNameTargetInputSchema,
    phone: saPhoneNumberSchema.optional().nullable(),
    defaultRateMinorUnits: z.int().min(1, 'Rate must be positive').optional().nullable(),
    defaultRateCurrency: z.string().length(3).optional(),
    source: EntitySourceSchema.optional(),
    status: ClientStatusSchema.optional(),
    emails: z.array(CreateClientEmailInputSchema).max(CREATE_CLIENT_MAX_EMAILS, `At most ${CREATE_CLIENT_MAX_EMAILS} email addresses can be added when creating a client`).optional(),
});
export const UpdateClientInputSchema = z.object({
    name: z.string().min(1, 'Client name is required').max(200, 'Name must be 200 characters or less').optional(),
    businessEntity: ClientBusinessEntityInputSchema.optional(),
    nameTarget: ClientNameTargetInputSchema,
    phone: saPhoneNumberSchema.optional().nullable(),
    defaultRateMinorUnits: z.int().min(1, 'Rate must be positive').optional().nullable(),
    defaultRateCurrency: z.string().length(3).optional(),
    status: ClientStatusSchema.optional(),
});
/**
 * Response for `PUT .../clients/:clientId/business-entity`: the updated
 * `Client`, plus the shared `warnings` / `warningDetails` pair a write
 * carrying `vatNumber` can populate (see `vatCheckDigitWarning` in
 * `common.js`).
 */
export const ClientWriteResponseSchema = ClientSchema.extend(writeWarningsFields);
export const UpsertClientBusinessEntityResponseSchema = ClientWriteResponseSchema;
export const ClientSortDirectionSchema = z.enum(['asc', 'desc']);
export const ClientSortFieldSchema = z.enum(['name', 'createdAt', 'lastActivityDate']);
export const ListClientsQuerySchema = z.object({
    includeArchived: z.enum(['true', 'false']).optional(),
    archivedOnly: z.enum(['true', 'false']).optional(),
    ids: z.array(z.uuid()).max(100).optional(),
    search: z.string().max(200).optional(),
    sortBy: ClientSortFieldSchema.optional(),
    sortDirection: ClientSortDirectionSchema.optional(),
    status: ClientStatusSchema.optional(),
}).extend(PaginationQuerySchema.shape);
export const ClientListResponseSchema = createPaginatedResponseSchema(ClientSchema);
export const DeleteClientResponseSchema = z.object({
    success: z.boolean(),
});
// =============================================================================
// Client Project
// =============================================================================
export const ClientProjectSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    clientId: z.string(),
    name: z.string(),
    customReference: z.string().nullable(),
    // NOT NULL post-tightening migration; auto-allocated for every matter.
    internalReference: z.string(),
    description: z.string().nullable(),
    status: ClientProjectStatusSchema,
    defaultRateMinorUnits: z.number().nullable(),
    currency: z.string(),
    source: EntitySourceSchema,
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /**
     * Sum of APPROVED activity `amountMinorUnits` for this matter, from the
     * stored financial source of truth. Null on the matter LIST when
     * `includeActivitySummary=true` was not requested, on mutation responses,
     * and always on the single-matter GET (identity only - never computed
     * there). 0 or more when computed (`includeActivitySummary=true` on the
     * list). Matter-level WIP is read via the dedicated
     * `GET .../client-projects/:clientProjectId/wip-total` endpoint instead of
     * this field on a single-matter GET.
     */
    approvedAmountMinorUnits: z.number().nullable(),
    /** Same convention as `approvedAmountMinorUnits`, summing DRAFT activities instead. */
    draftAmountMinorUnits: z.number().nullable(),
    /**
     * Combined DRAFT+APPROVED unbilled activity value for this matter - "what
     * can I bill", the invoice-builder's question, not "what is WIP" (which is
     * APPROVED-only; see `ClientWipTotalResponseSchema`). Computed server-side
     * so the UI reads one figure instead of summing two. Equals
     * `approvedAmountMinorUnits + draftAmountMinorUnits`; same null convention
     * as those fields, including on the single-matter GET.
     */
    unbilledTotalMinorUnits: z.number().nullable(),
    /**
     * WIP for this matter - APPROVED activity only, matching the dashboard's
     * definition and `GET .../client-projects/:clientProjectId/wip-total`
     * (`findWipTotalForProject`). Equals `approvedAmountMinorUnits`; unlike
     * `unbilledTotalMinorUnits` (DRAFT+APPROVED, "what can I bill"), this is
     * "what is WIP" - the figure the client page and matter summary card show.
     * Same null convention as `approvedAmountMinorUnits`: null unless
     * `includeActivitySummary=true` was requested on the matter LIST.
     */
    wipTotalMinorUnits: z.number().nullable(),
    /** Null on the matter LIST without `includeActivitySummary=true`, on mutation responses, and always on the single-matter GET (not computed there). */
    latestActivityDate: z.string().nullable(),
    /** Same null convention as `latestActivityDate`. */
    approvedActivitiesCount: z.number().nullable(),
    /** Same null convention as `latestActivityDate`. */
    draftActivitiesCount: z.number().nullable(),
    /**
     * Sum of subtotalMinorUnits across APPROVED unbilled attributed expenses.
     * Deliberately unbounded by date - a disbursement is a cost already
     * incurred, so a future-dated one is still billable now. Null when
     * includeExpenseSummary=true was not requested. 0 when summary was
     * requested but no qualifying expenses exist. Required-nullable to match
     * sibling summary fields (approvedAmountMinorUnits etc.)
     */
    unbilledExpenseSubtotalMinorUnits: z.number().nullable(),
    /**
     * How many APPROVED unbilled attributed expenses the matter carries - the
     * count behind `unbilledExpenseSubtotalMinorUnits`, and the same set the
     * invoice editor auto-adds when the matter is selected. Same null
     * convention as that field: null unless includeExpenseSummary=true, 0 when
     * requested with nothing qualifying.
     */
    unbilledExpenseCount: z.number().nullable(),
    /**
     * YYYY-MM-DD date of the most recent APPROVED unbilled attributed expense.
     * Null when includeExpenseSummary=true was not requested, or when no
     * qualifying expenses exist. Separate from latestActivityDate so callers
     * can distinguish activity recency from expense recency.
     */
    latestExpenseDate: z.string().nullable(),
    clientName: z.string(),
});
// Block ASCII control chars (C0 + DEL). The label flows verbatim into PDFs,
// emails, CSV exports and HTML tables; control characters break all four
// surfaces in different ways (line breaks in PDFs, header injection in emails,
// row corruption in CSVs, parser drift in HTML). Printable Unicode is fine -
// the label is human-readable and may legitimately carry punctuation,
// non-Latin scripts, etc.
// Done via charCodeAt rather than a regex literal to keep the source free of
// ESLint's `no-control-regex` warning; the runtime cost is comparable for
// short strings.
function containsAsciiControlChar(s) {
    for (let i = 0; i < s.length; i++) {
        const code = s.charCodeAt(i);
        if (code < 0x20 || code === 0x7f)
            return true;
    }
    return false;
}
const CUSTOM_REFERENCE_CONTROL_CHAR_MESSAGE = 'Custom reference cannot contain control characters';
const customReferenceField = nullableText(100, 'Custom reference must be 100 characters or less')
    .refine((v) => v === null || v === undefined || !containsAsciiControlChar(v), { error: CUSTOM_REFERENCE_CONTROL_CHAR_MESSAGE });
export const CreateClientProjectInputSchema = z.strictObject({
    idempotencyKey: z.string().min(1).max(200),
    clientId: z.uuid({ error: 'Client ID must be a UUID' }).toLowerCase(),
    name: z.string().min(1, 'Project name is required').max(200, 'Name must be 200 characters or less'),
    customReference: customReferenceField,
    description: nullableText(2000, 'Description must be 2000 characters or less'),
    status: ClientProjectStatusSchema.optional(),
    defaultRateMinorUnits: z.int().min(1, 'Rate must be positive').optional().nullable(),
    currency: z.string().length(3, 'Currency must be a 3-letter ISO code').optional().default('ZAR'),
    source: EntitySourceSchema.optional(),
});
export const UpdateClientProjectInputSchema = z.object({
    name: z.string().min(1, 'Project name is required').max(200, 'Name must be 200 characters or less').optional(),
    customReference: customReferenceField,
    description: nullableText(2000, 'Description must be 2000 characters or less'),
    status: ClientProjectStatusSchema.optional(),
    defaultRateMinorUnits: z.int().min(1, 'Rate must be positive').optional().nullable(),
    currency: z.string().length(3, 'Currency must be a 3-letter ISO code').optional(),
});
/**
 * Display rule: prefer the user-defined customReference when set; fall back to
 * the system-generated internal reference (always present post-tightening
 * migration).
 */
export function getProjectDisplayReference(project) {
    const custom = project.customReference?.trim();
    if (custom)
        return custom;
    return project.internalReference;
}
export const ClientProjectSortFieldSchema = z.enum(['name', 'internalReference', 'clientName', 'createdAt']);
export const ListClientProjectsQuerySchema = z.object({
    names: z.array(z.string().trim().min(1).max(200)).min(1).max(100).optional(),
    includeArchived: z.enum(['true', 'false']).optional(),
    archivedOnly: z.enum(['true', 'false']).optional(),
    includeActivitySummary: z.enum(['true', 'false']).optional(),
    includeExpenseSummary: z.enum(['true', 'false']).optional(),
    hasPendingActivities: z.enum(['true', 'false']).optional(),
    clientId: z.union([z.uuid(), z.array(z.uuid()).max(100)]).optional(),
    ids: z.array(z.uuid()).max(100).optional(),
    search: z.string().max(200).optional(),
    sortField: ClientProjectSortFieldSchema.optional(),
    sortDirection: ClientSortDirectionSchema.optional(),
    status: ClientProjectStatusSchema.optional(),
}).extend(PaginationQuerySchema.shape);
export const ClientProjectListResponseSchema = createPaginatedResponseSchema(ClientProjectSchema);
export const DeleteClientProjectResponseSchema = z.object({
    success: z.boolean(),
});
// =============================================================================
// Client Project Activity
// =============================================================================
export const ClientProjectActivityTypeSchema = z.enum([
    'MEETING',
    'EMAIL',
    'PHONE',
    'MESSAGE',
    'COURT_ATTENDANCE',
    'DOCUMENT_REVIEW',
    'RESEARCH',
    'DRAFTING',
    'TRAVEL_CAR',
    'PRINTING',
    'POSTAGE',
    'ADVOCATE',
    'CORRESPONDENT_ATTORNEY',
    'SHERIFF',
    'GOVERNMENT_GAZETTE',
    'GENERAL',
]);
export const ClientProjectActivityStatusSchema = z.enum(['DRAFT', 'APPROVED', 'INVOICED']);
// Per-type narration patterns (formerly ACTIVITY_DESCRIPTION_PATTERNS here) now
// live in `@packages/copy`'s `activity:descriptionPatterns.*` catalog keys, so
// the guidance can vary by practice dialect. The backend's
// `formatActivityDescriptionPatterns` (agents/chat/activity-patterns.ts)
// renders them for the chat system prompt and the
// `createClientProjectActivity` MCP tool description.
// =============================================================================
// Activity Attachments
// =============================================================================
/**
 * MIME types allowed for activity attachments.
 * Exported for frontend validation reuse.
 *
 * Office formats are restricted to the modern OOXML variants (.docx/.xlsx/.pptx).
 * Legacy OLE formats (.doc/.xls, application/msword, application/vnd.ms-excel)
 * and macro-enabled variants (.docm/.xlsm/.pptm) are intentionally excluded because
 * they can carry VBA macros - a well-known malware vector. OOXML without the
 * macro-enabled content type cannot embed executable VBA.
 */
export const ALLOWED_ACTIVITY_ATTACHMENT_MIME_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/csv',
];
/** Browser MIME reporting for CSV and PowerPoint is inconsistent; the server validates bytes. */
export function isAllowedGeneralAttachmentFile(file) {
    const filename = file.name.toLowerCase();
    if (filename.endsWith('.csv') || filename.endsWith('.pptx'))
        return true;
    return ALLOWED_ACTIVITY_ATTACHMENT_MIME_TYPES.some((mimeType) => mimeType === file.type && mimeType !== 'text/csv' && mimeType !== 'application/vnd.openxmlformats-officedocument.presentationml.presentation');
}
export const GENERAL_ATTACHMENT_ACCEPT = [...ALLOWED_ACTIVITY_ATTACHMENT_MIME_TYPES, '.csv', '.pptx'].join(',');
export const GENERAL_ATTACHMENT_SUMMARY_LABEL = 'PDF, Word, Excel, PowerPoint, CSV or images';
export const GENERAL_ATTACHMENT_FORMATS_LABEL = 'PDF, Word (.docx), Excel (.xlsx), PowerPoint (.pptx), CSV (.csv), JPEG, PNG, WebP or HEIC';
export const ActivityDocumentResponseSchema = z.object({
    id: z.string(),
    activityId: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    downloadUrl: z.string(),
});
export const ActivityDocumentListResponseSchema = z.array(ActivityDocumentResponseSchema);
// =============================================================================
// Client Documents
// =============================================================================
export const ClientDocumentResponseSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    downloadUrl: z.string(),
});
export const ClientDocumentListResponseSchema = z.array(ClientDocumentResponseSchema);
/** Longest file note a time entry takes: room for full meeting minutes. */
export const MAX_ACTIVITY_NOTE_LENGTH = 20_000;
const ActivityNoteSchema = z.string().max(MAX_ACTIVITY_NOTE_LENGTH, `Note must be ${formatCount(MAX_ACTIVITY_NOTE_LENGTH)} characters or less`);
export const ClientProjectActivitySchema = z.object({
    id: z.string(),
    accountId: z.string(),
    clientProjectId: z.string(),
    performedByUserId: z.string().nullable(),
    /**
     * The user who created this activity, or null for legacy rows and system-created activities.
     * Drives per-member visibility scoping: DRAFT activities are only visible to their creator.
     */
    createdByUserId: z.string().uuid().nullable(),
    type: ClientProjectActivityTypeSchema.nullable(),
    units: z.number(),
    ratePerUnitMinorUnits: z.number(),
    /**
     * Pre-VAT line total in minor units: `round(units * ratePerUnitMinorUnits)`.
     * The stored financial source of truth - server-computed at every write path
     * (create, update, calendar auto-creation, CSV import) and never derived from
     * the account's billing increment. The increment is an input-time steering aid
     * only (see time-increment.ts): once a row is written, this is the only amount
     * figure, so the chat agent and UI both confirm the same authoritative total
     * rather than re-deriving the product (LLM arithmetic on fractional units is
     * error-prone).
     */
    amountMinorUnits: z.number().int(),
    unitType: UnitTypeSchema,
    isDisbursement: z.boolean(),
    description: z.string(),
    /** Internal file note, never on the invoice. Null when the entry has none. */
    note: z.string().nullable(),
    /** When the note was last written, or null if it never has been. */
    noteUpdatedAt: z.iso.datetime().nullable(),
    date: z.string(),
    status: ClientProjectActivityStatusSchema,
    // Always set. An activity always carries a monetary value, so by invariant
    // it always carries a currency. The server defaults it from the parent
    // project at create time when the caller omits one.
    currency: z.string(),
    source: EntitySourceSchema,
    sourceRef: z.string().nullable(),
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    attachmentCount: z.number(),
    clientProjectName: z.string(),
    clientName: z.string(),
    /**
     * The invoice line item this activity is currently attached to, or null if not on any invoice.
     * Used by the bill UI to classify activities as "addable" vs "moveable" in batch toggle flows.
     */
    invoiceLineItemId: z.string().nullable(),
    /**
     * ISO 8601 timestamp of when the linked calendar event was cancelled, or null if not cancelled.
     * Populated by enrichActivities; null for non-calendar activities.
     */
    linkedEventCancelledAt: z.iso.datetime().nullable(),
    /**
     * ISO 8601 timestamp of when the linked calendar event was rescheduled, or null if not rescheduled.
     * Populated by enrichActivities; null for non-calendar activities.
     */
    linkedEventRescheduledAt: z.iso.datetime().nullable(),
});
/**
 * Response shape for the two routes that can steer input-time behaviour:
 * create and update. `warnings` is `[]` when the write raised nothing to flag,
 * and carries one entry when the row's hours are not a multiple of the
 * account's billing increment - the route computes it via
 * `isHoursMultipleOfIncrement` (see `time-increment.ts`); the increment is
 * never consulted by list/get, since those responses report a row already
 * written, not a moment to steer. Entries are plain-English text (durations
 * and increment labels only, no PII).
 */
export const MutatedClientProjectActivitySchema = ClientProjectActivitySchema.extend({
    warnings: z.array(z.string()),
});
// The `unitType` and `isDisbursement` pair on each row is the authoritative
// source of how the activity is billed. The registry in activity-types.ts
// only supplies defaults for the wizard / chat agent; once a row is created,
// the columns are what count.
//
// `unitType` is required on create - the wizard, chat agent pass it explicitly;
// CSV import gets the registry default via the optional bulk schema.
//
// The service layer enforces that `unitType` is in
// `ACTIVITY_TYPE_REGISTRY[type].allowedUnitTypes` for typed activities. That
// cross-field invariant can't be expressed in JSON Schema, so ajv does not
// evaluate it, and no `.refine()` on this schema states it either - the service
// is the only place it lives. Note the reason is NOT that a refine would be
// inert: the framework re-parses through Zod after ajv, so one would run (see
// `src/api/CLAUDE.md`). It belongs in the service because the invariant must
// also hold for callers that never touch a wire schema.
export const CreateClientProjectActivityInputSchema = z.object({
    idempotencyKey: z.string().min(1, 'Idempotency key is required').max(255, 'Idempotency key must be 255 characters or less'),
    clientProjectId: z.string().min(1, 'Project ID is required'),
    performedByUserId: z.uuid('performedByUserId must be a valid UUID, not an email or name').nullable().optional().describe('Team member who did the work. Omitted, it is the current user - or the account owner when an assistant (human or AI) is capturing. Set it only when logging for a DIFFERENT named member ("log for James"). null means no fee earner: send it ONLY when the user says there is no fee earner, for example work by someone who has left. Never send null for an unremarked fee.'),
    description: z.string().min(1, 'Description is required').max(2000, 'Description must be 2000 characters or less').describe('The line the client sees on the invoice.'),
    note: ActivityNoteSchema.optional().describe('Internal file note (meeting minutes, attendees, detail backing the entry up). Never on the invoice. Only from detail the user gave; omit otherwise.'),
    date: CalendarDateSchema.describe('YYYY-MM-DD (or ISO date-time); today unless the user gave a date.'),
    units: z.number().min(0, 'Units must be 0 or greater').describe('Quantity in unitType, only as the user stated it: decimal hours (30 min = 0.5), kilometres, or a whole count of pages / appearances / items / documents. 1 for lump_sum.'),
    ratePerUnitMinorUnits: z.int().min(0, 'Rate must be 0 or greater').describe('Per-unit rate in cents (rand x 100: R90 is 9000, R1,500 is 150000). For lump_sum, the full amount.'),
    // Optional on the wire: when omitted the server defaults it from the parent
    // project's currency. Never null - omit it to accept the default.
    currency: z.string().length(3, 'Currency must be a 3-letter ISO code').optional(),
    source: EntitySourceSchema.optional(),
    sourceRef: nullableText(500, 'Source reference must be 500 characters or less').describe('Id of the external record the entry came from (an email or WhatsApp message id). Omit for entries dictated in chat. Never a generated UUID - it is not an idempotency key.'),
    type: ClientProjectActivityTypeSchema.describe(`The most specific type; GENERAL only when nothing else fits. ${formatDeprecatedActivityTypeGuidance()}`),
    unitType: UnitTypeSchema.describe(`Billing unit, per type:\n${formatUnitTypeDefaultsAndOverrides()}\nOther pairs are rejected. Override the default only when the user signals it ("flat R1500 for travel" -> lump_sum; "bill the travel as 2 hours" -> hours); it pre-fills a quantity the user gave, never a reason to invent one.`),
    isDisbursement: z.boolean().optional().describe(`Pass-through cost flag. ${formatIsDisbursementDefaults()} Set it only when the user explicitly contradicts the default.`),
});
/**
 * Precondition on a write to one time entry (If-Match style): the `updatedAt`
 * the caller last read. When given for an entry, the write is refused as a
 * `stale-version` conflict if the entry has changed since - see `staleVersion`
 * in `client-project-activity.helpers.ts`.
 */
const ExpectedUpdatedAtSchema = z.iso.datetime();
export const UpdateClientProjectActivityInputSchema = z.strictObject({
    // `null` clears the performer, on fees and disbursements alike.
    performedByUserId: z.uuid('performedByUserId must be a valid UUID').nullable().optional(),
    description: z.string().min(1, 'Description is required').max(2000, 'Description must be 2000 characters or less').optional(),
    date: CalendarDateSchema.describe('YYYY-MM-DD (or ISO date-time).').optional(),
    status: ClientProjectActivityStatusSchema.optional(),
    units: z.number().min(0, 'Units must be 0 or greater').optional().describe('Replacement quantity in the entry\'s unitType, only when the user gave a new one.'),
    ratePerUnitMinorUnits: z.int().min(0, 'Rate must be 0 or greater').optional().describe('Per-unit rate in cents (R90 is 9000). For lump_sum, the full amount with units=1.'),
    type: ClientProjectActivityTypeSchema.nullable().optional().describe("Correctable until INVOICED; a new type re-validates unitType against it."),
    unitType: UnitTypeSchema.optional().describe("Must be allowed for the row's new or current type. Immutable once INVOICED."),
    isDisbursement: z.boolean().optional().describe('Immutable once INVOICED.'),
    // Optional to change; never null - currency is a required invariant on the row.
    currency: z.string().length(3, 'Currency must be a 3-letter ISO code').optional(),
    clientProjectId: z.uuid('clientProjectId must be a valid UUID').optional(),
    /** Server-set precondition, hidden from the chat agent (`mcp.hiddenFields`): the write is refused as `stale-version` if the entry changed since. */
    expectedUpdatedAt: ExpectedUpdatedAtSchema.optional(),
});
/**
 * Replace (or, with null, clear) a time entry's file note. Allowed at every
 * status, INVOICED included: the note is not billed, so locking the entry does
 * not lock it.
 */
export const SetClientProjectActivityNoteInputSchema = z.strictObject({
    note: ActivityNoteSchema.nullable(),
});
/** Most time entries one approve call takes. */
export const MAX_APPROVE_ACTIVITY_IDS = 50;
export const ApproveClientProjectActivitiesInputSchema = z.object({
    activityIds: z
        .array(LowercaseUuidSchema)
        .min(1)
        .max(MAX_APPROVE_ACTIVITY_IDS)
        .refine((ids) => new Set(ids).size === ids.length, 'activityIds must not repeat an id')
        .describe('Every DRAFT entry the user asked you to approve.'),
    expectedUpdatedAt: z
        .array(z.object({ id: LowercaseUuidSchema, updatedAt: ExpectedUpdatedAtSchema }))
        .optional()
        .describe('Optional version check: list an entry only when you have read its updatedAt (from listClientProjectActivities, createClientProjectActivity or updateClientProjectActivity) and want the approve refused (409) if it changed since. Otherwise omit it; approving without it still succeeds.'),
}).refine((input) => {
    if (!input.expectedUpdatedAt)
        return true;
    const ids = input.expectedUpdatedAt.map((entry) => entry.id);
    const approving = new Set(input.activityIds);
    return new Set(ids).size === ids.length && ids.every((id) => approving.has(id));
}, { message: 'expectedUpdatedAt must name each entry at most once, and only entries in activityIds', path: ['expectedUpdatedAt'] });
/** The entries, in the order `activityIds` gave them. */
export const ApproveClientProjectActivitiesResponseSchema = z.object({
    activities: z.array(ClientProjectActivitySchema),
});
// `resolveRateFromTotal`: given a total the user stated and, when they stated
// one, the quantity it covers, resolve the exact per-unit rate (or fall back to
// a lump sum when the total does not divide the quantity into whole cents; a
// total sent on its own resolves to the lump-sum shape directly) - see
// `resolveClientProjectActivityFieldsFromTotal` in the client-project-activity
// domain, which this route delegates to.
export const ResolveRateFromTotalQuerySchema = z.object({
    totalMinorUnits: z.coerce.number().int().min(0).max(MAX_MINOR_UNITS)
        .describe('The stated line total in cents (R722.50 is 72250).'),
    units: z.coerce.number().positive().max(MAX_UNITS).optional()
        .describe('The quantity the user stated, decimals allowed (2.5 hours, 0.5 km). Never one you worked out by dividing the total by a per-km or per-page rate: that quantity is invented, even when it comes out exact.'),
    unitType: UnitTypeSchema.exclude(['lump_sum']).optional()
        .describe('Unit of the stated quantity. Send units and unitType together, or neither when the user stated only a total; one alone, or lump_sum, is rejected.'),
});
export const ResolveRateFromTotalResponseSchema = z.object({
    units: z.number().describe('The stated quantity, or 1 when the total did not divide evenly and the response fell back to a lump sum.'),
    unitType: UnitTypeSchema.describe("The stated unit type, or 'lump_sum' on the fallback."),
    ratePerUnitMinorUnits: z.int().describe('The exact per-unit rate in minor units, or the full total when the response fell back to a lump sum.'),
    amountMinorUnits: z.int().describe('Always equal to the requested totalMinorUnits.'),
    warnings: z.array(z.string()).describe('One entry naming the total and the quantity when the response fell back to a lump sum; empty otherwise.'),
});
/**
 * A filter that accepts either a single value (?clientId=a) or repeated params
 * (?clientId=a&clientId=b), parsed as string | string[]. Capped at 100 entries.
 */
const ActivityFilterIdListParam = z.union([z.string(), z.array(z.string()).max(100)]).optional();
/**
 * Every FILTER field shared by the activity list and the activity totals
 * endpoints - narrows which activities are in scope, independent of
 * pagination, sort, or (for totals) grouping. `ListClientProjectActivitiesQuerySchema`
 * and `AggregateClientProjectActivitiesQuerySchema` both extend this shape so the
 * two endpoints accept identical filters by construction rather than by two
 * hand-kept lists that can drift.
 */
export const ClientProjectActivityFiltersQuerySchema = z.object({
    ids: z.array(LowercaseUuidSchema).max(100).optional(),
    status: z
        .union([ClientProjectActivityStatusSchema, z.array(ClientProjectActivityStatusSchema).max(3)])
        .optional(),
    source: EntitySourceSchema.optional(),
    excludeSource: EntitySourceSchema.optional(),
    includeArchived: z.enum(['true', 'false']).optional(),
    isDisbursement: z.enum(['true', 'false']).optional(),
    notInvoiced: z.enum(['true', 'false']).optional(),
    includeUpcoming: z.enum(['true', 'false']).optional(),
    /**
     * Inclusive lower / upper bounds on the activity's `date` (the day the work
     * was performed), not on `createdAt`. Accepts a bare calendar day
     * (`YYYY-MM-DD`) or a full ISO datetime; activity dates are stored at
     * midnight UTC, so a bare day bound is inclusive of that whole day.
     *
     * The repository has supported these filters since it was written
     * (`ActivityListFilters.dateFrom` / `dateTo` in
     * `client-project-activity.repository.ts`) - they were simply never exposed
     * on the querystring, so every caller that wanted a date-bounded read had to
     * fetch broadly and filter in application code. For the chat agent that meant
     * pulling a page of raw activity JSON into the prompt and picking rows by
     * eye, which is both expensive and unreliable on a month-end billing run.
     *
     * `dateTo` interacts with `includeUpcoming`: future-dated activities are
     * hidden by default, so an effective upper bound of `min(dateTo, now)` is
     * applied unless `includeUpcoming=true`. See `buildWhereClause`.
     */
    dateFrom: CalendarDateSchema.optional(),
    dateTo: CalendarDateSchema.optional(),
    clientId: ActivityFilterIdListParam,
    clientProjectId: ActivityFilterIdListParam,
    createdByUserId: ActivityFilterIdListParam,
    performedByUserId: ActivityFilterIdListParam,
    sourceRef: z.string().optional(),
    /**
     * Scope to the activity bound to a specific EmailClientMatch row. Used by
     * the email modal's activity preview so it tracks the current match
     * rather than any archived activity that shares the email's sourceRef.
     */
    sourceEmailMatchId: z.uuid().optional(),
    search: z.string().max(200).optional(),
    /**
     * Filter by activity type. Accepts a single value or repeated params for match-any
     * (e.g. ?type=MEETING&type=EMAIL). Omit to return all types.
     */
    type: z.union([ClientProjectActivityTypeSchema, z.array(ClientProjectActivityTypeSchema).max(15)]).optional(),
});
// Strict: a filter or sort the route does not have (the agent has sent
// `sortField` and `unitType`) is a 400 naming it, not an unfiltered page
// presented as filtered.
export const ListClientProjectActivitiesQuerySchema = z.strictObject({
    ...PaginationQuerySchema.shape,
    ...ClientProjectActivityFiltersQuerySchema.shape,
    /**
     * Direction for the date sort. Defaults to 'desc' (newest first).
     * Activities are always sorted by date; this controls whether newest or oldest
     * appears first. createdAt is always the secondary tiebreaker (same direction).
     */
    sortDirection: ClientSortDirectionSchema.optional(),
});
export const ClientProjectActivityListResponseSchema = createPaginatedResponseSchema(ClientProjectActivitySchema);
/**
 * Dimensions `aggregateClientProjectActivities` can group by. `clientProject` and
 * `performedByUser` map onto the row's `clientProjectId` / `performedByUserId`
 * FK columns; the rest are 1:1 with their scalar column. `client` is the one
 * dimension with no column of its own on the activity row (the client id lives
 * on the matter): the reader groups by matter and the service rolls the matters
 * up to their clients, so the roll-up is never left to whoever reads the groups.
 */
export const AggregateClientProjectActivitiesDimensionSchema = z.enum([
    'client',
    'clientProject',
    'performedByUser',
    'status',
    'type',
    'isDisbursement',
]);
export const AggregateClientProjectActivitiesQuerySchema = createAggregateQuerySchema(ClientProjectActivityFiltersQuerySchema, AggregateClientProjectActivitiesDimensionSchema);
export const AggregateClientProjectActivitiesResponseSchema = createAggregateResponseSchema({
    // `count` is the number of activities. Figures that must never be added
    // together stay apart: `amounts` carries one entry per currency, `units` one
    // entry per unitType (hours are never summed with km, pages or a lump sum).
    measures: z.object({
        amounts: z.array(z.object({
            currency: z.string(),
            amountMinorUnits: z.int(),
        })),
        units: z.array(z.object({
            unitType: UnitTypeSchema,
            units: z.number(),
        })),
    }),
    key: z.object({
        clientId: z.string().optional(),
        clientProjectId: z.string().optional(),
        clientProjectName: z.string().optional(),
        clientName: z.string().optional(),
        performedByUserId: z.string().nullable().optional(),
        // Absent when the person has since left the account.
        performedByUserName: z.string().optional(),
        status: ClientProjectActivityStatusSchema.optional(),
        // Never null: an entry with no stored type is a General entry, exactly as
        // the list's `type=GENERAL` filter treats it.
        type: ClientProjectActivityTypeSchema.optional(),
        isDisbursement: z.boolean().optional(),
    }),
    filter: z.object({
        ids: z.array(z.string()).nullable(),
        status: z.array(ClientProjectActivityStatusSchema).nullable(),
        source: EntitySourceSchema.nullable(),
        excludeSource: EntitySourceSchema.nullable(),
        includeArchived: z.boolean(),
        isDisbursement: z.boolean().nullable(),
        notInvoiced: z.boolean().nullable(),
        includeUpcoming: z.boolean(),
        dateFrom: z.string().nullable(),
        dateTo: z.string().nullable(),
        clientId: z.array(z.string()).nullable(),
        clientProjectId: z.array(z.string()).nullable(),
        createdByUserId: z.array(z.string()).nullable(),
        performedByUserId: z.array(z.string()).nullable(),
        sourceRef: z.string().nullable(),
        sourceEmailMatchId: z.string().nullable(),
        search: z.string().nullable(),
        type: z.array(ClientProjectActivityTypeSchema).nullable(),
    }),
    dimension: AggregateClientProjectActivitiesDimensionSchema,
});
export const ArchiveClientProjectActivityResponseSchema = z.object({
    success: z.boolean(),
});
/**
 * Response for the super-admin bulk "archive all of an account's non-invoiced
 * activities" operation (every client, every matter). `archivedCount` is the
 * number of active DRAFT/APPROVED activities archived in the call (or, when
 * `dryRun` is true, the number that WOULD be archived); `skippedInvoicedCount`
 * is the number of active INVOICED activities left untouched (their
 * invoice-line traceability is preserved) in either mode. Both are >= 0.
 * `dryRun` echoes the request mode: `true` means no write and no events were
 * made (a preview only); `false` means the archive actually ran. A re-run
 * with `dryRun: false` against the same account returns `archivedCount: 0`
 * once nothing non-INVOICED remains. Covered work on a live pro forma refuses
 * the whole call (409) rather than skipping those rows.
 */
export const ArchiveAccountActivitiesResponseSchema = z.object({
    archivedCount: z.int().min(0),
    skippedInvoicedCount: z.int().min(0),
    dryRun: z.boolean(),
});
